import { MemoryServer, RepwiseStore, SupabaseTransport, SyncEngine, startAutoSync, type SyncTransport } from '@repwise/sync';
import { diag } from '../diag/log.ts';
import { supabaseAuth, type AuthPort, type AuthUser } from './auth.ts';

/**
 * App wiring. With Supabase configured, an account is required (ADR-0016); without it (development and tests)
 * the app runs locally with no gate. Only the public publishable key belongs in the client.
 */
export interface Repo {
  store: RepwiseStore;
  engine: SyncEngine;
  auth: AuthPort | null;
  user: () => AuthUser | null;
  /** Explicit sign-out: re-arms the hard gate, keeps local data (quarantined), detaches this device's alerts. */
  signOut: () => Promise<void>;
  /** After a successful code verification. */
  signedIn: (u: AuthUser) => Promise<void>;
  onAuth: (cb: () => void) => () => void;
  /** Erase everything Repwise keeps on this device and sign out. Callers reload the app afterwards. */
  wipeDevice: () => Promise<void>;
  /** Save in-app feedback (submit_feedback RPC). null when there's no backend (development). */
  sendFeedback: ((kind: string, body: string, context: Record<string, unknown>) => Promise<{ ok: true } | { ok: false; error: 'offline' | 'signed_out' | 'rate_limited' | 'failed' }>) | null;
  stop: () => void;
}

export async function createRepo(opts: { dbName?: string; transport?: SyncTransport; auth?: AuthPort | null; autoSync?: boolean; sendFeedback?: Repo['sendFeedback'] } = {}): Promise<Repo> {
  const store = await RepwiseStore.open(opts.dbName ?? 'repwise');
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  let auth: AuthPort | null = opts.auth === undefined ? null : opts.auth;
  let transport = opts.transport;
  let sendFeedback: Repo['sendFeedback'] = opts.sendFeedback ?? null;
  if (opts.auth === undefined && url && key) {
    // detectSessionInUrl: a sign-in LINK landing on the app also works (desktop fallback); the typed code is primary
    // on phones because a link opens the system browser, whose storage is separate from the installed app.
    // Loaded only when sync is configured, keeping it off the gym-floor critical path (bundle budget).
    const client = (await import('@supabase/supabase-js')).createClient(url, key, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } });
    auth = supabaseAuth(client);
    transport ??= new SupabaseTransport(client);
    sendFeedback = async (kind, body, context) => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: false, error: 'offline' };
      if (!current) return { ok: false, error: 'signed_out' };
      try {
        const { error } = await client.rpc('submit_feedback', { p_kind: kind, p_body: body, p_context: context });
        if (!error) return { ok: true };
        return { ok: false, error: error.code === '54000' ? 'rate_limited' : 'failed' };
      } catch {
        return { ok: false, error: 'offline' };
      }
    };
  }
  let current: AuthUser | null = auth ? await auth.current() : null;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((l) => l());
  const engine = new SyncEngine(store, transport ?? new MemoryServer().transport(() => current?.id ?? null), { getUserId: () => current?.id ?? null, isOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false });

  auth?.onChange((u) => {
    current = u;
    void engine.bindUser().then(() => engine.drain());
    emit();
  });
  if (current) await engine.bindUser();
  const stop = opts.autoSync === false || typeof window === 'undefined' ? () => undefined : startAutoSync(engine, store);

  // ADR-0024: log sync state changes (not every status poll): label, breaker mode, dead letters
  {
    let prev = '';
    engine.onStatus(() => {
      void engine.status().then((st) => {
        const key = `${st.state}|${st.label}|${st.retry?.mode ?? ''}|${st.deadLetters}`;
        if (key === prev) return;
        prev = key;
        diag('sync', 'status', { state: st.state, label: st.label, pending: st.pending, deadLetters: st.deadLetters, mode: st.retry?.mode ?? null, failures: st.retry?.failures ?? null }, st.deadLetters ? 'warn' : 'info');
      }).catch(() => undefined);
    });
  }
  const repo: Repo = {
    store,
    engine,
    auth,
    user: () => current,
    onAuth: (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    async signedIn(u) {
      current = u;
      await store.setMeta('signedOut', false);
      await engine.bindUser();
      emit();
      void engine.drain();
    },
    async signOut() {
      await store.setMeta('signedOut', true);
      try {
        const { disablePush } = await import('./push.ts');
        await disablePush(repo);
      } catch {
        /* alerts were off or unsupported */
      }
      await auth?.signOut();
      current = null;
      emit();
      store.notify();
    },
    async wipeDevice() {
      try {
        const { deleteDiag } = await import('../diag/store.ts');
        await deleteDiag();
      } catch {
        /* nothing recorded */
      }
      try {
        sessionStorage.setItem('repwise-wiped', '1'); // read once by the gate's Shell (J8): survives the sign-out and reload
      } catch {
        /* storage unavailable */
      }
      try {
        const { disablePush } = await import('./push.ts');
        await disablePush(repo);
      } catch {
        /* alerts were off or unsupported */
      }
      stop();
      await auth?.signOut();
      current = null;
      await store.destroy();
      try {
        for (const k of Object.keys(localStorage)) if (k.startsWith('repwise.') || k.startsWith('sb-')) localStorage.removeItem(k);
      } catch {
        /* storage unavailable */
      }
    },
    sendFeedback,
    stop,
  };
  return repo;
}
